import confetti from 'canvas-confetti';
import i18n from 'i18next';

import { COLORS, TILE_STATUSES } from '@/pages/learningPath/Constants';
import { CONFETTI_COLORS } from '@/services/Constants';

import type { Step } from '@/pages/learningPath/types';

export const updateTileProgressStatus = (
	stepId: number | string,
	isCorrect: boolean,
	stateSteps: Step[],
): Step[] => {
	if (stateSteps.length === 0) return stateSteps;

	const steps = stateSteps.map((step) => ({ ...step }));
	const stepIndex = steps.findIndex((step) => step.id == stepId);

	if (stepIndex === -1) return steps;

	// Update correct or wrong answers count
	const step = steps[stepIndex];
	if (isCorrect) {
		step.correct_answers_count = (step.correct_answers_count || 0) + 1;
	} else {
		step.wrong_answers_count = (step.wrong_answers_count || 0) + 1;
	}

	// Calculate progress and determine status
	const progress = Math.round(
		((step.correct_answers_count! + step.wrong_answers_count!) / step.total_questions_count!) *
			100,
	);
	step.progress = progress;
	step.status = progress === 100 ? TILE_STATUSES.TILE_COMPLETED : TILE_STATUSES.TILE_IN_PROGRESS;

	// Update previous steps if the current step is in progress
	if (step.status === TILE_STATUSES.TILE_IN_PROGRESS) {
		steps.forEach((prevStep, index) => {
			if (
				index < stepIndex &&
				[TILE_STATUSES.TILE_IN_PROGRESS, TILE_STATUSES.TILE_LOCKED].includes(
					prevStep.status,
				)
			) {
				prevStep.status = TILE_STATUSES.TILE_COMPLETED;
			}
		});
	}

	// Update next steps if the current step is completed
	if (step.status === TILE_STATUSES.TILE_COMPLETED) {
		for (let i = stepIndex + 1; i < steps.length; i++) {
			const nextStep = steps[i];
			if (nextStep?.status === TILE_STATUSES.TILE_LOCKED) {
				if (
					nextStep.is_daily_goal_flag ||
					nextStep.is_skipped_tile ||
					nextStep.is_free_play_tile
				) {
					nextStep.status = TILE_STATUSES.TILE_COMPLETED;
				} else {
					nextStep.status = TILE_STATUSES.TILE_IN_PROGRESS;
					break;
				}
			} else {
				break;
			}
		}
	}

	return steps;
};

export const showFireworksConfetti = (id?: string, duration: number = 3000): void => {
	const animationEnd = Date.now() + duration;
	const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: -1 };
	let confettiTool = confetti;

	if (id) {
		const canvas = document.getElementById(id) as HTMLCanvasElement | undefined;
		confettiTool = (canvas as any)?.confetti || confettiTool.create(canvas, { resize: true });
	}

	const randomInRange = (min: number, max: number): number => {
		return Math.random() * (max - min) + min;
	};

	const interval = setInterval(() => {
		const timeLeft = animationEnd - Date.now();

		if (timeLeft <= 0) {
			return clearInterval(interval);
		}

		const particleCount = 50 * (timeLeft / duration);
		confettiTool({
			...defaults,
			particleCount,
			colors: CONFETTI_COLORS,
			origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
		});
		confettiTool({
			...defaults,
			particleCount,
			colors: CONFETTI_COLORS,
			origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
		});
	}, 250);
};

export const checkDailyGoal = (sectionId: number | string, steps: Step[]): boolean => {
	if (!steps?.length || !sectionId) return false;

	const currentStepIndex = steps.findIndex(({ id }) => id == sectionId);
	const currentStep = steps[currentStepIndex];
	const nextStep = steps[currentStepIndex + 1];

	return !!(currentStep?.is_daily_goal || (nextStep?.is_skipped_tile && nextStep?.is_daily_goal));
};

export const isEmptyLearningPath = (learningPath: Step[]): boolean => {
	return !learningPath?.length || learningPath.length === 1;
};

export const addDailyGoalFlag = (steps: Step[]): Step[] => {
	let isDailyGoalTileAdded = false;
	return steps.reduce((acc: Step[], step, index) => {
		if (!isDailyGoalTileAdded) {
			step.is_part_of_daily_goal = true;
		}
		acc.push(step);
		if (step.is_daily_goal) {
			isDailyGoalTileAdded = true;
			let dailyGoalTileStatus = TILE_STATUSES.TILE_LOCKED;
			if (step.is_skipped_tile) {
				dailyGoalTileStatus =
					steps[index - 1]?.status === TILE_STATUSES.TILE_COMPLETED
						? TILE_STATUSES.TILE_COMPLETED
						: TILE_STATUSES.TILE_LOCKED;
			} else if (step.status === TILE_STATUSES.TILE_COMPLETED) {
				dailyGoalTileStatus = TILE_STATUSES.TILE_COMPLETED;
			}

			acc.push({
				id: `${step.id}-daily-goal`,
				status: dailyGoalTileStatus,
				color: COLORS.BLUE,
				is_daily_goal_flag: true,
				position_index: step.position_index ?? 0,
			});
		}
		return acc;
	}, []);
};

export const addFreePlayTile = (steps: Step[]): Step[] => {
	return steps.reduce((acc: Step[], step, index) => {
		acc.push(step);
		if (step.is_daily_goal_flag) {
			step.has_skip_line = true;
			acc.push({
				id: 'free-play-tile',
				status: step.status,
				color: step.color,
				is_free_play_tile: true,
				position_index: step.position_index ?? 0,
			});
		}
		return acc;
	}, []);
};

export const prefillOnboardingData = (learningPath: Step[]): Step[] => {
	return learningPath.map((step) => {
		const {
			is_onboarding_tile,
			is_new_difficulty_onboarding_tile,
			is_sticker_onboarding_tile,
		} = step;

		if (is_onboarding_tile) {
			return {
				...step,
				id: 'onboarding_tile',
				title: i18n.t('learning_path.onboarding_title'),
				image: '🤩',
				color: COLORS.ORANGE,
			};
		}

		if (is_new_difficulty_onboarding_tile) {
			return {
				...step,
				id: 'new_difficulty_onboarding_tile',
				title: i18n.t('learning_path.new_difficulty_onboarding_title'),
				image: '🌱',
				color: COLORS.TURQUOISE,
			};
		}

		if (is_sticker_onboarding_tile) {
			return {
				...step,
				id: 'sticker_onboarding_tile',
			};
		}

		return step;
	});
};

export const addTileLinesData = (steps: Step[]): Step[] => {
	steps.forEach((step, index) => {
		if (step.is_skipped_tile && index > 0) {
			steps[index - 1].has_skip_line = true;
		}
		if (step.is_intervened_section) {
			step.has_mistake_line = true;
		}
	});
	return steps;
};

export const updateTileLinesColor = (steps: Step[]): Step[] => {
	return steps.map((currentStep, index) => {
		const nextStep = steps[index + 1];

		const currentStepColor =
			currentStep.status === TILE_STATUSES.TILE_LOCKED
				? COLORS.GREY
				: currentStep?.color ?? COLORS.GREY;

		const nextStepColor =
			!nextStep || nextStep.status === TILE_STATUSES.TILE_LOCKED
				? COLORS.GREY
				: nextStep.color ?? COLORS.GREY;

		return {
			...currentStep,
			line_colors: [currentStepColor, nextStepColor],
		};
	});
};

export const updateTilePositionIndex = (steps: Step[]): Step[] => {
	return steps.map((step, index) => {
		return {
			...step,
			position_index: index % 7,
		};
	});
};

export const updateLearningPath = (learningPath: Step[], hasFreePlay: boolean): Step[] => {
	learningPath = addDailyGoalFlag(learningPath);
	if (hasFreePlay) {
		learningPath = addFreePlayTile(learningPath);
	}
	learningPath = prefillOnboardingData(learningPath);
	learningPath = addTileLinesData(learningPath);
	learningPath = updateTileLinesColor(learningPath);
	learningPath = updateTilePositionIndex(learningPath);
	return learningPath;
};

export const checkGameSection = (sectionId: number | string, steps: Step[]): boolean => {
	if (!steps?.length || !sectionId) return false;

	const currentStepIndex = steps.findIndex(({ id }) => id == sectionId);
	const currentStep = steps[currentStepIndex];
	return !!currentStep?.game_id;
};

export const finishTileProgress = (stepId: number | string, stateSteps: Step[]): Step[] => {
	if (stateSteps.length === 0) return stateSteps;

	const steps = stateSteps.map((step) => ({ ...step }));
	const stepIndex = steps.findIndex((step) => step.id == stepId);

	if (stepIndex === -1) return steps;

	// Mark the tile as completed with 100% progress
	const step = steps[stepIndex];
	step.progress = 100;
	step.status = TILE_STATUSES.TILE_COMPLETED;

	// Update previous steps if they are still in progress or locked
	steps.forEach((prevStep, index) => {
		if (
			index < stepIndex &&
			[TILE_STATUSES.TILE_IN_PROGRESS, TILE_STATUSES.TILE_LOCKED].includes(prevStep.status)
		) {
			prevStep.status = TILE_STATUSES.TILE_COMPLETED;
		}
	});

	// Update next steps - unlock the next tile
	for (let i = stepIndex + 1; i < steps.length; i++) {
		const nextStep = steps[i];
		if (nextStep?.status === TILE_STATUSES.TILE_LOCKED) {
			if (
				nextStep.is_daily_goal_flag ||
				nextStep.is_skipped_tile ||
				nextStep.is_free_play_tile
			) {
				nextStep.status = TILE_STATUSES.TILE_COMPLETED;
			} else {
				nextStep.status = TILE_STATUSES.TILE_IN_PROGRESS;
				break;
			}
		} else {
			break;
		}
	}

	return steps;
};
